"use client";

import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { loadIssuePhoto, deleteIssuePhoto } from "@/lib/issue-photo";

interface PhotoCaptureSheetProps {
  blockId: Id<"blocks">;
  photoPath: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
  onCancelled?: () => void;
}

export function PhotoCaptureSheet({
  blockId,
  photoPath,
  open,
  onOpenChange,
  onSaved,
  onCancelled,
}: PhotoCaptureSheetProps) {
  const [note, setNote] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const addPhoto = useMutation(api.issuePhotos.add);

  // Load image preview
  useEffect(() => {
    if (open && photoPath) {
      loadIssuePhoto(photoPath).then(setImageUrl);
    }
    // Reset state when closed
    if (!open) {
      setImageUrl(null);
      setNote("");
    }
  }, [open, photoPath]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await addPhoto({
        blockId,
        path: photoPath,
        note: note.trim() || undefined,
      });
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save photo:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    // Delete the captured photo file since user cancelled
    try {
      await deleteIssuePhoto(photoPath);
    } catch (error) {
      console.warn("Failed to delete cancelled photo:", error);
    }
    onCancelled?.();
    onOpenChange(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleCancel();
      }}
    >
      <SheetContent side="bottom" className="h-[85vh]">
        <SheetHeader>
          <SheetTitle>Add Photo</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full pt-4 pb-6 gap-4">
          {/* Photo Preview */}
          <div className="flex-1 min-h-0 rounded-lg overflow-hidden bg-muted">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Captured photo"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Note Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Note <span className="text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              placeholder="Describe the issue..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Save Photo
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
